import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dbHelpers } from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import TradeForm from './TradeForm'
import TradesList from './TradesList'
import * as FiIcons from 'react-icons/fi'

const { FiPlus, FiFilter, FiSearch, FiAlertCircle } = FiIcons

const Journal = () => {
  const { user } = useAuth()
  const location = useLocation()
  const [trades, setTrades] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingTrade, setEditingTrade] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterBy, setFilterBy] = useState('all')
  const [error, setError] = useState('')

  useEffect(() => {
    if (user) {
      loadTrades()
    }
    
    // Check if we should open the form automatically (from Dashboard/Insights navigation)
    if (location.state?.openForm) {
      setShowForm(true)
      // Clear the state so it doesn't reopen on refresh
      window.history.replaceState({}, document.title)
    }
  }, [user, location.state])

  const loadTrades = async () => {
    try {
      setLoading(true)
      const tradesData = await dbHelpers.getUserTrades(user.id, 100)
      setTrades(tradesData)
    } catch (error) {
      console.error('Error loading trades:', error)
      setError('Failed to load trades. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  const handleTradeSubmit = async (tradeData) => {
    try {
      setError('')
      if (editingTrade) {
        await dbHelpers.updateTrade(editingTrade.id, tradeData)
      } else {
        await dbHelpers.addTrade({ ...tradeData, user_id: user.id })
      }
      await loadTrades()
      setShowForm(false)
      setEditingTrade(null)
    } catch (error) {
      console.error('Error saving trade:', error)
      setError(error.message || 'Failed to save trade. Please check your inputs.')
    }
  }

  const handleEditTrade = (trade) => {
    setEditingTrade(trade)
    setShowForm(true)
  }

  const filteredTrades = trades.filter(trade => {
    const symbolMatch = trade.symbol?.toLowerCase().includes(searchTerm.toLowerCase())
    const setupMatch = trade.setup_type?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesSearch = symbolMatch || setupMatch
    
    const matchesFilter = filterBy === 'all' || 
      (filterBy === 'wins' && (trade.pnl || 0) > 0) || 
      (filterBy === 'losses' && (trade.pnl || 0) < 0)
      
    return matchesSearch && matchesFilter
  })

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trading Journal</h1>
          <p className="text-gray-600 mt-1">Record and track your trading activities</p>
        </div>
        <button 
          onClick={() => { setError(''); setShowForm(true); }} 
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md"
        >
          <SafeIcon icon={FiPlus} className="h-4 w-4" />
          <span>Add Trade</span>
        </button>
      </motion.div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 text-red-700">
          <SafeIcon icon={FiAlertCircle} className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Filters */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.1 }} 
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
            </div>
            <input 
              type="text" 
              value={searchTerm} 
              onChange={(e) => setSearchTerm(e.target.value)} 
              placeholder="Search by symbol or setup..." 
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" 
            />
          </div>
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiFilter} className="h-5 w-5 text-gray-400" />
            <select 
              value={filterBy} 
              onChange={(e) => setFilterBy(e.target.value)} 
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Trades</option>
              <option value="wins">Winning Trades</option>
              <option value="losses">Losing Trades</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Trade Form Modal */}
      {showForm && (
        <TradeForm 
          trade={editingTrade} 
          onSubmit={handleTradeSubmit} 
          onClose={() => { setShowForm(false); setEditingTrade(null); }} 
        />
      )}

      {/* Trades List */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.2 }}
      >
        <TradesList trades={filteredTrades} loading={loading} onEditTrade={handleEditTrade} />
      </motion.div>
    </div>
  )
}

export default Journal